<template>
  <div>
    <!-- Topbar Desktop -->
    <div class="topbar-area d-lg-block d-none">
      <div class="container">
        <div class="topbar-wrap">
          <div class="logo-and-search-area">
            <router-link :to="buildRoute('/')" class="header-logo1">
              <img :src="flavor.logos.header" :alt="flavor.displayName">
            </router-link>
            <form v-if="showSearchBar" class="search-area" @submit.prevent="handleSearch" ref="searchFormRef">
              <div class="form-inner">
                <button type="submit">
                  <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                    <g>
                      <path d="M15.8044 14.8855L13.0544 12.198L12.99 12.1002C12.8688 11.9807 12.7055 11.9137 12.5353 11.9137C12.3651 11.9137 12.2018 11.9807 12.0806 12.1002C9.74343 14.2443 6.14312 14.3605 3.66561 12.3724C1.18811 10.3843 0.604677 6.90645 2.30061 4.24832C3.99655 1.5902 7.44655 0.573637 10.3631 1.87332C13.2797 3.17301 14.755 6.38739 13.8125 9.38239C13.7793 9.48905 13.7753 9.60268 13.8011 9.71137C13.8269 9.82007 13.8815 9.91983 13.9591 10.0002C14.0375 10.082 14.1358 10.1421 14.2443 10.1746C14.3528 10.2071 14.4679 10.211 14.5784 10.1858C14.6883 10.1616 14.79 10.109 14.8732 10.0332C14.9564 9.95744 15.0182 9.86113 15.0525 9.75395C16.1775 6.19989 14.4781 2.37489 11.0525 0.75395C7.62686 -0.866988 3.50468 0.200824 1.35124 3.26864C-0.802198 6.33645 -0.34001 10.4818 2.43905 13.0239C5.21811 15.5661 9.47968 15.7408 12.4687 13.4377L14.9037 15.8183C15.026 15.9358 15.1889 16.0014 15.3584 16.0014C15.5279 16.0014 15.6909 15.9358 15.8131 15.8183C15.8728 15.7599 15.9201 15.6902 15.9525 15.6133C15.9848 15.5363 16.0015 15.4537 16.0015 15.3702C16.0015 15.2867 15.9848 15.2041 15.9525 15.1271C15.9201 15.0502 15.8728 14.9805 15.8131 14.9221L15.8044 14.8855Z"/>
                    </g>
                  </svg>
                </button>
                <input 
                  v-model="searchQuery" 
                  type="text" 
                  placeholder="Find Your Perfect PG"
                  @focus="suggestionsOpen = true"
                  @blur="handleSearchBlur"
                  autocomplete="off"
                >
              </div>
              <!-- Search Autocomplete Dropdown -->
              <Transition name="dropdown-fade">
                <div v-if="suggestionsOpen && (suggestions.length > 0 || searchLoading)" class="search-autocomplete-dropdown">
                  <div v-if="searchLoading" class="autocomplete-loading">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                    <span>Searching...</span>
                  </div>
                  <ul v-else class="autocomplete-list">
                    <li 
                      v-for="(property, index) in suggestions" 
                      :key="property.id"
                      class="autocomplete-item"
                      :style="{ animationDelay: `${index * 50}ms` }"
                      @mousedown.prevent="handleSuggestionClick(property)"
                    >
                      <img 
                        v-if="property.image" 
                        :src="property.image" 
                        :alt="property.name"
                        class="autocomplete-item-img"
                      >
                      <div class="autocomplete-item-info">
                        <span class="autocomplete-item-name">{{ property.name }}</span>
                        <span class="autocomplete-item-location">{{ property.location }}</span>
                      </div>
                    </li>
                  </ul>
                </div>
              </Transition>
            </form>
          </div>
          <div class="topbar-right">
            <div class="support-and-language-area">
              <router-link :to="buildRoute('/contact')">Need Help?</router-link>
            </div>
            <!-- Logged-in: user dropdown -->
            <div v-if="authStore.isAuthenticated" ref="userDropdownRef" class="user-dropdown-area">
              <button type="button" class="user-dropdown-trigger" @click.stop="toggleUserDropdown">
                <span class="user-avatar-circle">
                  {{ userInitial }}
                </span>
                <span class="user-name-label">{{ userName }}</span>
                <i class="bi bi-caret-down-fill" style="font-size:10px"></i>
              </button>
              <ul class="user-dropdown-menu" :class="{ active: isUserDropdownOpen }">
                <li class="dropdown-user-info">
                  <strong>{{ userName }}</strong>
                  <small>{{ authStore.session?.user?.mobile }}</small>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li>
                  <router-link :to="myProfileRoute" class="dropdown-item" @click="closeUserDropdown">
                    <i class="bi bi-person-circle me-2"></i>My Profile
                  </router-link>
                </li>
                <li>
                  <router-link :to="myBookingsRoute" class="dropdown-item" @click="closeUserDropdown">
                    <i class="bi bi-calendar-check me-2"></i>My Bookings
                  </router-link>
                </li>
                <li>
                  <router-link :to="myInquiriesRoute" class="dropdown-item" @click="closeUserDropdown">
                    <i class="bi bi-inbox me-2"></i>My Inquiries
                  </router-link>
                </li>
                <li v-if="canOpenManagementApp">
                  <button type="button" class="dropdown-item" @click="handleGoToDashboard">
                    <i class="bi bi-speedometer2 me-2"></i>Go to Dashboard
                  </button>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li>
                  <button type="button" class="dropdown-item" @click="handleLogout">
                    <i class="bi bi-box-arrow-right me-2"></i>Logout
                  </button>
                </li>
              </ul>
            </div>

            <!-- Not logged-in: login button -->
            <button v-else type="button" class="primary-btn1 black-bg login-trigger-btn" @click="openLoginModal">
              <span>
                <svg width="15" height="15" viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
                  <g>
                    <path d="M7.50105 7.78913C9.64392 7.78913 11.3956 6.03744 11.3956 3.89456C11.3956 1.75169 9.64392 0 7.50105 0C5.35818 0 3.60652 1.75169 3.60652 3.89456C3.60652 6.03744 5.35821 7.78913 7.50105 7.78913ZM14.1847 10.9014C14.0827 10.6463 13.9467 10.4082 13.7936 10.1871C13.0113 9.0306 11.8038 8.2653 10.4433 8.07822C10.2732 8.06123 10.0861 8.09522 9.95007 8.19727C9.23578 8.72448 8.38546 8.99658 7.50108 8.99658C6.61671 8.99658 5.76638 8.72448 5.05209 8.19727C4.91603 8.09522 4.72895 8.04421 4.5589 8.07822C3.19835 8.2653 1.97387 9.0306 1.20857 10.1871C1.05551 10.4082 0.919443 10.6633 0.817424 10.9014C0.766415 11.0034 0.783407 11.1225 0.834416 11.2245C0.970484 11.4626 1.14054 11.7007 1.2936 11.9048C1.53168 12.2279 1.78679 12.517 2.07592 12.7891C2.31401 13.0272 2.58611 13.2483 2.85824 13.4694C4.20177 14.4728 5.81742 15 7.48409 15C9.15076 15 10.7664 14.4728 12.1099 13.4694C12.382 13.2653 12.6541 13.0272 12.8923 12.7891C13.1644 12.517 13.4365 12.2279 13.6746 11.9048C13.8446 11.6837 13.9977 11.4626 14.1338 11.2245C14.2188 11.1225 14.2358 11.0034 14.1847 10.9014Z"/>
                  </g>
                </svg>
                Login
              </span>
              <span>
                <svg width="15" height="15" viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
                  <g>
                    <path d="M7.50105 7.78913C9.64392 7.78913 11.3956 6.03744 11.3956 3.89456C11.3956 1.75169 9.64392 0 7.50105 0C5.35818 0 3.60652 1.75169 3.60652 3.89456C3.60652 6.03744 5.35821 7.78913 7.50105 7.78913ZM14.1847 10.9014C14.0827 10.6463 13.9467 10.4082 13.7936 10.1871C13.0113 9.0306 11.8038 8.2653 10.4433 8.07822C10.2732 8.06123 10.0861 8.09522 9.95007 8.19727C9.23578 8.72448 8.38546 8.99658 7.50108 8.99658C6.61671 8.99658 5.76638 8.72448 5.05209 8.19727C4.91603 8.09522 4.72895 8.04421 4.5589 8.07822C3.19835 8.2653 1.97387 9.0306 1.20857 10.1871C1.05551 10.4082 0.919443 10.6633 0.817424 10.9014C0.766415 11.0034 0.783407 11.1225 0.834416 11.2245C0.970484 11.4626 1.14054 11.7007 1.2936 11.9048C1.53168 12.2279 1.78679 12.517 2.07592 12.7891C2.31401 13.0272 2.58611 13.2483 2.85824 13.4694C4.20177 14.4728 5.81742 15 7.48409 15C9.15076 15 10.7664 14.4728 12.1099 13.4694C12.382 13.2653 12.6541 13.0272 12.8923 12.7891C13.1644 12.517 13.4365 12.2279 13.6746 11.9048C13.8446 11.6837 13.9977 11.4626 14.1338 11.2245C14.2188 11.1225 14.2358 11.0034 14.1847 10.9014Z"/>
                  </g>
                </svg>
                Login
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Header with Navigation -->
    <header class="style-1">
      <div class="container d-flex flex-nowrap align-items-center justify-content-between">
        <router-link :to="buildRoute('/')" class="header-logo d-lg-none d-block">
          <img :src="flavor.logos.headerMobile" :alt="flavor.displayName">
        </router-link>

        <MegaMenu />

        <div class="nav-right d-lg-flex d-none">
          <router-link :to="buildRoute('/contact')" class="header-contact-link">
            <i class="bi bi-headset"></i>
            <span>Contact Us</span>
          </router-link>
        </div>
        
        <div class="d-flex align-items-center gap-2 d-lg-none">
          <!-- Mobile: logged-in user avatar -->
          <button
            v-if="authStore.isAuthenticated"
            type="button"
            class="mobile-user-btn"
            @click="toggleUserDropdown"
          >
            <span class="user-avatar-circle small">{{ userInitial }}</span>
          </button>
          <!-- Mobile: login button -->
          <button
            v-else
            type="button"
            class="mobile-login-btn"
            @click="openLoginModal"
          >
            <i class="bi bi-person-circle" style="font-size:22px"></i>
          </button>
          <div class="sidebar-button mobile-menu-btn">
            <i class="bi bi-list"></i>
          </div>
        </div>
      </div>
    </header>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import MegaMenu from './MegaMenu.vue'
import { resolveFlavor } from '@/config/flavors'
import { useAuthModalStore } from '@/stores/authModal'
import { useAuthStore } from '@/stores/auth'
import { useAccountRoute } from '@/composables/useAccountRoute'
import { useSearchAutocomplete } from '@/composables/useSearchAutocomplete'
import { useManagementApp } from '@/composables/useManagementApp'

const router = useRouter()
const route = useRoute()
const { buildRoute } = useAccountRoute()
const flavor = inject('flavor', resolveFlavor())

// Show search bar only on home and property-list screens
const showSearchBar = computed(() => {
  const routeName = route.name
  // Also check slug-prefixed routes (slug-home, slug-property-list)
  return routeName === 'home' || routeName === 'slug-home' || 
         routeName === 'property-list' || routeName === 'slug-property-list'
})

// Search autocomplete
const {
  query: searchQuery,
  suggestions,
  isLoading: searchLoading,
  isOpen: suggestionsOpen,
  selectSuggestion,
  close: closeSuggestions
} = useSearchAutocomplete({ debounceMs: 300, minChars: 2, maxResults: 6 })

const searchFormRef = ref(null)
const authModalStore = useAuthModalStore()
const authStore = useAuthStore()
const isUserDropdownOpen = ref(false)
const userDropdownRef = ref(null)

const userName = computed(() => authStore.session?.user?.fullName || 'User')
const userInitial = computed(() => {
  const name = userName.value
  return name ? name.charAt(0).toUpperCase() : 'U'
})

// Management app (dashboard) navigation
const { canOpenManagementApp, goToManagementApp } = useManagementApp()

// Handler when user clicks a suggestion
const handleSuggestionClick = (property) => {
  selectSuggestion(property)
  router.push(buildRoute({
    name: 'property-details',
    params: {
      slug: property.slug,
      propertyId: property.id || undefined
    }
  }))
}

// Close suggestions on blur (with small delay to allow click)
const handleSearchBlur = () => {
  setTimeout(() => {
    closeSuggestions()
  }, 150)
}

const toggleUserDropdown = () => {
  isUserDropdownOpen.value = !isUserDropdownOpen.value
}

const closeUserDropdown = () => {
  isUserDropdownOpen.value = false
}

const myProfileRoute = computed(() => buildRoute({ name: 'my-profile' }))
const myBookingsRoute = computed(() => buildRoute({ name: 'my-bookings' }))
const myInquiriesRoute = computed(() => buildRoute({ name: 'my-inquiries' }))

const handleGoToDashboard = () => {
  closeUserDropdown()
  goToManagementApp()
}

const handleLogout = () => {
  isUserDropdownOpen.value = false
  authStore.signOut()
  router.push(buildRoute({ name: 'home' }))
}

const handleDocumentClick = (event) => {
  const userRoot = userDropdownRef.value
  if (!userRoot || !userRoot.contains(event.target)) {
    isUserDropdownOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})

const openLoginModal = () => {
  authModalStore.open('signin')
}

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    router.push(buildRoute({
      name: 'property-list',
      query: { search: searchQuery.value }
    }))
  }
}
</script>

<style scoped>
/* Ensure topbar stacks above header nav (z-index: 999) so user dropdown is visible */
.topbar-area {
  position: relative;
  z-index: 1000;
}

.login-trigger-btn {
  border: none;
}

/* Prevent tall brand logos from stretching the header area. */
.header-logo1 {
  flex-shrink: 0;
}

.header-logo1 img {
  display: block;
  width: auto;
  max-width: 120px;
  max-height: 72px;
  object-fit: contain;
}

.header-logo img {
  display: block;
  width: auto;
  max-width: 120px;
  max-height: 56px;
  object-fit: contain;
}

/* ── User dropdown ─────────────────────────────────────────── */
.user-dropdown-area {
  position: relative;
}

.user-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e6e6e6;
  border-radius: 40px;
  background: #fff;
  padding: 6px 14px 6px 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: #222;
}

.user-avatar-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #0d6efd;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  flex-shrink: 0;
}

.user-avatar-circle.small {
  width: 28px;
  height: 28px;
  font-size: 13px;
}

.user-name-label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  list-style: none;
  margin: 0;
  padding: 8px 0;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  display: none;
  z-index: 100;
}

.user-dropdown-menu.active {
  display: block;
}

.dropdown-user-info {
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
}

.dropdown-user-info strong {
  font-size: 14px;
  color: #222;
}

.dropdown-user-info small {
  font-size: 12px;
  color: #888;
}

.dropdown-divider {
  margin: 4px 0;
}

.user-dropdown-menu .dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 16px;
  border: 0;
  background: none;
  font-size: 14px;
  color: #222;
  cursor: pointer;
}

.user-dropdown-menu .dropdown-item:hover {
  background: #f5f5f5;
}

/* ── Mobile auth buttons ───────────────────────────────────── */
.mobile-login-btn,
.mobile-user-btn {
  border: 0;
  background: none;
  padding: 4px;
  cursor: pointer;
  color: #222;
}

/* ── Header contact link ───────────────────────────────────── */
.header-contact-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: 1px solid #e6e6e6;
  border-radius: 40px;
  background: #fff;
  color: #222;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.header-contact-link:hover {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.header-contact-link i {
  font-size: 16px;
}

/* ── Search Autocomplete Dropdown ─────────────────────────── */
.search-area {
  position: relative;
}

.search-autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  max-height: 320px;
  overflow-y: auto;
}

.autocomplete-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: #666;
  font-size: 14px;
}

.autocomplete-list {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}

.autocomplete-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  animation: item-slide-in 0.2s ease-out both;
}

.autocomplete-item:hover {
  background: #f5f8ff;
}

.autocomplete-item-img {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.autocomplete-item-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.autocomplete-item-name {
  font-weight: 600;
  font-size: 14px;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.autocomplete-item-location {
  font-size: 12px;
}

/* ── Dropdown Transitions ─────────────────────────────────── */
.dropdown-fade-enter-active {
  animation: dropdown-in 0.2s ease-out;
}

.dropdown-fade-leave-active {
  animation: dropdown-out 0.15s ease-in;
}

@keyframes dropdown-in {
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes dropdown-out {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-8px);
  }
}

@keyframes item-slide-in {
  0% {
    opacity: 0;
    transform: translateX(-10px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.autocomplete-item-location {
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
